import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Box } from "@mui/material";
import { $applyNodeReplacement, $getNodeByKey } from "lexical";
import { useCallback } from "react";
import { useFlag } from "../../../../server/optimizely";
import { resolveDividerStyle } from "../types/divider-style";
import { useSelectable } from "../utils/use-selectable";
import { $isDividerNode, DividerNode } from "./divider-node";
import { DividerStyleToolbar } from "./divider-style-toolbar";
import { useDividerTemplate } from "./divider-template-context";
import { convertDividerElement, isDividerElement } from "./utils";

import type { InsertDividerCommand } from "../types/commands";
import type { ChangeDividerStyle, DividerStyleOverride } from "../types/divider-style";
import type { SerializedDividerNode } from "./divider-node";
import type { DOMConversionMap, NodeKey } from "lexical";
import type { FC } from "react";

const DIVIDER_STYLE_EDITING_FLAG = "cha_4006_divider_style_editing";

function historyOptions(skipHistory: boolean): { tag?: "historic" } {
  if (skipHistory) {
    return { tag: "historic" };
  }
  return {};
}

// skipHistory keeps in-progress drags out of undo, so one drag is one undo step.
function useDividerStyleChange(nodeKey: NodeKey): ChangeDividerStyle {
  const [editor] = useLexicalComposerContext();

  return useCallback(
    (change, skipHistory) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isDividerNode(node)) {
          return;
        }
        node.setStyleOverride({ ...node.getStyleOverride(), ...change });
      }, historyOptions(skipHistory));
    },
    [editor, nodeKey],
  );
}

const DividerComponent: FC<{
  nodeKey: NodeKey;
  divider: InsertDividerCommand;
  styleOverride: DividerStyleOverride;
}> = ({ nodeKey, divider, styleOverride }) => {
  const isSelected = useSelectable(nodeKey);
  const dividers = useDividerTemplate();
  const [styleEditingEnabled] = useFlag(DIVIDER_STYLE_EDITING_FLAG);
  const [editor] = useLexicalComposerContext();
  // Lexical still selects nodes on click in read-only editors, so check editability too.
  const showStyleToolbar = styleEditingEnabled && editor.isEditable() && isSelected && divider;
  const handleStyleChange = useDividerStyleChange(nodeKey);

  return (
    <Box
      className="editor-hr"
      sx={[
        { position: "relative", border: "none", borderColor: "primary.main", p: "3px" },
        isSelected && { border: "solid 1px" },
      ]}
    >
      {showStyleToolbar && (
        <DividerStyleToolbar
          dividerStyle={resolveDividerStyle({
            type: divider.type,
            dividers,
            override: styleOverride,
          })}
          onChange={handleStyleChange}
        />
      )}
      <Box
        component="hr"
        className={divider?.type}
        style={{
          borderTopColor: styleOverride.color,
          borderTopWidth: styleOverride.width,
          borderTopStyle: styleOverride.style,
        }}
      />
    </Box>
  );
};

export class DividerNodeMui extends DividerNode {
  decorate(): React.JSX.Element {
    return (
      <DividerComponent
        nodeKey={this.__key}
        divider={this.__divider}
        styleOverride={this.__styleOverride}
      />
    );
  }
  static importJSON(node: SerializedDividerNode): DividerNode {
    return super.importJSON(node);
  }
  exportJSON(): SerializedDividerNode {
    return super.exportJSON();
  }
  static clone(node: DividerNode): DividerNode {
    return super.clone(node);
  }
  static getType(): string {
    return super.getType();
  }

  static importDOM(): DOMConversionMap | null {
    return {
      hr: (node: HTMLElement) => {
        if (!isDividerElement(node)) return null;

        return {
          conversion: (n) => convertDividerElement(n, DividerNodeMui),
          priority: 0,
        };
      },
    };
  }
}

export function $createDividerNode(headless: boolean, data?: InsertDividerCommand): DividerNode {
  return $applyNodeReplacement(new (headless ? DividerNode : DividerNodeMui)(data));
}
